import {
  CIMSPage,
  SharePointParentFolder,
  alphabeticalCompare,
  alphabeticalSort,
} from '@cims-online/shared'
import { CIMSColumn } from 'components/CIMSTable/CIMSColumn/types'
import { useConcreteTestSetTrackerContext } from '../../ConcreteTestSetTrackerContext'
import DocumentChecklistCheckboxes from './components/DocumentChecklistCheckboxes'
import { HelpMessage } from 'utils/constants'
import BreakDataList from './components/BreakDataList'
import { breakDataSearchFilter } from './utils'
import ProjectLink from 'components/ProjectLink'
import { dateFormatter } from 'utils/dateFormatter'
import { ConcreteTestSet } from '../../ConcreteTestSetTrackerContext/types'
import { useExternalLinks } from 'components/CIMSTable/common/hooks/useExternalLinks'

export function useColumns(): CIMSColumn<ConcreteTestSet>[] {
  const { documentChecklistItems } = useConcreteTestSetTrackerContext()
  const help = HelpMessage.Concrete.ConcreteTestSetTracker
  return [
    {
      title: 'Test Set ID',
      render: 'testSetId',
      sort: { with: 'key', key: 'testSetId', priority: 3 },
      filter: { type: 'search', with: 'key', key: 'testSetId' },
      width: 155,
      help: help.TestSetID,
    },
    {
      title: 'Date',
      render: ({ placementDate }) => dateFormatter.simple(placementDate),
      sort: {
        with: 'key',
        key: 'placementDate',
        default: 'descend',
        priority: 2,
      },
      width: 115,
      help: help.Date,
      filter: { type: 'date', with: 'key', key: 'placementDate' },
    },
    {
      title: 'Placement Type',
      render: ({ concreteMaterial }) => concreteMaterial?.placementType,
      sort: {
        with: 'callback',
        onCompare: (a, b) =>
          alphabeticalCompare(
            a.concreteMaterial,
            b.concreteMaterial,
            'placementType'
          ),
      },
      filter: {
        type: 'default',
        with: 'callback',
        items: (records) =>
          records.map(
            ({ concreteMaterial }) => concreteMaterial?.placementType ?? ''
          ),
        onFilter: (value, { concreteMaterial }) =>
          concreteMaterial?.placementType === value,
      },
      width: 185,
      help: help.Material.PlacementType,
    },
    {
      title: 'Mix Design',
      help: help.MixDesign.Number,
      width: 155,
      render: ({ concreteMixDesign }) =>
        concreteMixDesign && (
          <ProjectLink
            item={CIMSPage.ConcreteMixDesigns}
            deep={{
              recordId: concreteMixDesign.globalId,
              deleted: !concreteMixDesign.active,
            }}
          >
            {concreteMixDesign.mixDesignNumber}
          </ProjectLink>
        ),
      sort: {
        with: 'callback',
        onCompare: (a, b) =>
          alphabeticalCompare(
            a.concreteMixDesign,
            b.concreteMixDesign,
            'mixDesignNumber'
          ),
      },
      filter: {
        type: 'search',
        with: 'callback',
        onFilter: (value, { concreteMixDesign }) =>
          concreteMixDesign?.mixDesignNumber
            ?.toLowerCase()
            .includes(value.toString().toLowerCase()) ?? false,
      },
    },
    {
      title: 'Status',
      render: 'status',
      help: help.Status,
      sort: {
        with: 'callback',
        onCompare: (a, b) => {
          const Priority: Record<string, number> = {
            'Re-Opened': 2,
            Open: 1,
            Closed: 3,
          }
          return alphabeticalCompare(
            Priority[a.status!] ?? 1,
            Priority[b.status!] ?? 1
          )
        },
        priority: 1,
      },
      filter: {
        type: 'default',
        with: 'callback',
        items: (records) => records.map(({ status }) => status ?? 'Open'),
        onFilter: (value, { status }) => value === (status ?? 'Open'),
      },
      width: 130,
    },
    {
      title: 'Break Data',
      type: 'group',
      columns: [
        {
          title: 'Cylinders',
          align: 'center',
          width: { withoutRecords: 130, withRecords: 200 },
          render: ({ concreteTests }) => (
            <BreakDataList
              dataSource={concreteTests}
              renderItem={({
                strengthAge,
                fieldNumber,
                average28DayStrength,
              }) => ({
                label: fieldNumber ?? '',
                value: !!strengthAge ? average28DayStrength : undefined,
              })}
              sort={alphabeticalSort('fieldNumber')}
            />
          ),
          help: `${help.Cylinders}. Search by Test Sub ID, Cylinder Number, or Lab Number.`,
          filter: breakDataSearchFilter(({ concreteTests }) =>
            concreteTests.flatMap(
              ({ testSubId, cylinderNumber, concreteTestBreakData }) => [
                testSubId,
                cylinderNumber,
                ...concreteTestBreakData.map(({ labNumber }) => labNumber),
              ]
            )
          ),
        },
        {
          title: 'Cores',
          align: 'center',
          width: { withoutRecords: 110, withRecords: 200 },
          render: ({ concretePlacements }) => (
            <BreakDataList
              dataSource={concretePlacements.flatMap(
                ({ placementId, concreteCoreMeasurements }) =>
                  concreteCoreMeasurements.map((c) => ({ placementId, ...c }))
              )}
              renderItem={({
                placementId,
                measurementId,
                globalId,
                concreteCoreBreakData,
              }) => ({
                label:
                  placementId && measurementId
                    ? `${placementId}-${measurementId}`
                    : measurementId ?? placementId ?? globalId,
                value: concreteCoreBreakData?.strength ?? 0,
              })}
              sort={alphabeticalSort('measurementId')}
            />
          ),
          help: `${help.Cores}. Search by Placement ID, Measurement ID, full Core ID, or Lab Number.`,
          filter: breakDataSearchFilter(({ concretePlacements }) =>
            concretePlacements.flatMap(
              ({ placementId, concreteCoreMeasurements }) => {
                if (!concreteCoreMeasurements.length) return []
                return concreteCoreMeasurements.flatMap(
                  ({ measurementId, globalId, concreteCoreBreakData }) => [
                    placementId && measurementId
                      ? `${placementId}-${measurementId}`
                      : measurementId ?? placementId ?? globalId,
                    concreteCoreBreakData?.labNumber,
                  ]
                )
              }
            )
          ),
        },
      ],
    },
    {
      title: 'Document Checklist',
      render: (record) => <DocumentChecklistCheckboxes {...record} />,
      link: CIMSPage.DocumentChecklists,
      help: help.DocumentChecklist,
      width: 275,
      filter: {
        strict: true,
        type: 'checkboxes',
        items: (documentChecklistItems ?? []).map(({ name, id }) => ({
          text: name,
          value: id,
        })),
        value: (
          documentChecklistId,
          { documentChecklistValues, concreteMaterial }
        ) => {
          /**
           * `DocumentChecklistValue` records are only created when they are checked.
           * If no value is found for a given documentChecklistItemId, we need
           * to determine whether to return `false` or `undefined`. This depends
           * on whether the checklist item is associated with the concrete
           * material's placement type.
           */
          const hasChecklistItem = !!documentChecklistItems
            ?.find((item) => item.id === documentChecklistId)
            ?.documentChecklistItemToConcreteMaterials.some(
              ({ concreteMaterial: { placementType } }) => {
                return placementType === concreteMaterial?.placementType
              }
            )

          return (
            documentChecklistValues.find(
              ({ documentChecklistItem: { id } }) => id === documentChecklistId
            )?.checked ?? (hasChecklistItem ? false : undefined)
          )
        },
      },
    },
    useExternalLinks<ConcreteTestSet>({
      parent: SharePointParentFolder.ConcretePackets,
      dataFn: ({
        globalId,
        testSetId,
        sharePointResource,
        packetSharePointResource,
      }) => {
        return {
          recordId: globalId,
          externalLinks: [],
          folder: testSetId ?? globalId,
          resource: { webUrl: sharePointResource?.webUrl },
          packet: {
            name: packetSharePointResource?.name,
            webUrl: packetSharePointResource?.webUrl,
          },
        }
      },
    }),
  ]
}
